// SuperAdminDemoViewsPage.jsx
//
// Marketing analytics for the in-page "Watch demo" modal. Answers:
//
//   1. How many visitors open the demo?
//   2. What percentage make it all the way through?
//   3. Which scene loses the most viewers? (the drop-off funnel)
//   4. How long do they spend watching on average?
//   5. Where are visitors coming from? (top referrers)
//
// All metrics are computed server-side via the get_demo_view_stats RPC
// (migration 151). A second RPC returns the per-session feed for the
// "Recent views" table at the bottom of the page.

import React, { useEffect, useState } from 'react';
import {
  Eye, CheckCircle, Clock, TrendingUp, RefreshCw, AlertCircle,
  Sparkles, ChevronRight as ChevRight, Globe2,
} from 'lucide-react';
import { getDemoViewStats, listRecentDemoViews } from '../../lib/db';
import { formatDate } from '../../lib/formatters';

// Scene metadata — must match SCENES in MarketingHeroDemo.jsx. Kept in
// sync manually because the SuperAdmin page is a different bundle path
// than the marketing pages and we don't want to import marketing code
// into the SuperAdmin shell. Update both if you add/rename scenes.
const SCENE_LABELS = [
  'Intake',
  'Conflict check',
  'Case file',
  'Time',
  'Invoice',
  'Paid',
];

const RANGE_OPTIONS = [
  { value: 7,   label: 'Last 7 days' },
  { value: 30,  label: 'Last 30 days' },
  { value: 90,  label: 'Last 90 days' },
  { value: 365, label: 'Last year' },
];

export default function SuperAdminDemoViewsPage() {
  const [stats,    setStats]    = useState(null);
  const [recent,   setRecent]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [days,     setDays]     = useState(30);

  const refresh = () => {
    setLoading(true);
    setError('');
    Promise.all([
      getDemoViewStats(days),
      listRecentDemoViews(50),
    ]).then(([statsRes, recentRes]) => {
      if (statsRes.error) {
        setError(statsRes.error.message || 'Could not load stats');
      } else {
        // RPC returns an array of one row — unwrap it.
        const row = Array.isArray(statsRes.data) ? statsRes.data[0] : statsRes.data;
        setStats(row || null);
      }
      if (recentRes.error) {
        if (!error) setError(recentRes.error.message || 'Could not load recent views');
      } else {
        setRecent(recentRes.data || []);
      }
      setLoading(false);
    });
  };

  useEffect(refresh, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-white">Demo views</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            Analytics for the in-page "Watch demo" modal. Tracks how many
            visitors open the animated demo, which scenes they watch through,
            where they drop off, and how long they spend.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {RANGE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <button
            onClick={refresh}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium transition disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-900/30 border border-red-800 text-red-300 text-sm flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {loading && !stats && (
        <div className="text-center py-20 text-slate-500">Loading demo analytics…</div>
      )}

      {stats && stats.total_opens === 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-10 text-center">
          <Sparkles size={28} className="mx-auto text-slate-700 mb-3" />
          <p className="text-white font-semibold">No demo views in this range yet.</p>
          <p className="text-slate-500 text-sm mt-1.5 max-w-md mx-auto">
            When visitors click "Watch demo" on{' '}
            <a href="/#/" className="text-indigo-400 hover:underline">docketflowapp.com</a>,
            their session shows up here automatically.
          </p>
        </div>
      )}

      {stats && stats.total_opens > 0 && (
        <>
          {/* ── KPI cards ───────────────────────────────────────── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard
              icon={Eye}
              label="Demo opens"
              value={stats.total_opens.toLocaleString()}
              tone="blue"
            />
            <KpiCard
              icon={CheckCircle}
              label="Completed loops"
              value={stats.completed_loops.toLocaleString()}
              tone="emerald"
            />
            <KpiCard
              icon={TrendingUp}
              label="Completion rate"
              value={`${Math.round((Number(stats.completion_rate) || 0) * 100)}%`}
              tone="indigo"
            />
            <KpiCard
              icon={Clock}
              label="Avg time watching"
              value={`${Math.round(Number(stats.avg_dwell_seconds) || 0)}s`}
              tone="amber"
            />
          </div>

          {/* ── Scene funnel ───────────────────────────────────── */}
          <FunnelChart
            funnel={stats.scene_funnel || {}}
            totalOpens={stats.total_opens}
          />

          {/* ── Referrers ──────────────────────────────────────── */}
          <ReferrerBreakdown byReferrer={stats.by_referrer || {}} totalOpens={stats.total_opens} />
        </>
      )}

      {/* ── Recent sessions ─────────────────────────────────────── */}
      {recent.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
            <p className="text-sm font-semibold text-white">Recent sessions</p>
            <p className="text-xs text-slate-500">Latest {recent.length}</p>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-800/30">
              <tr>
                <th className="text-left px-5 py-2.5 font-medium">When</th>
                <th className="text-left px-5 py-2.5 font-medium">Reached</th>
                <th className="text-left px-5 py-2.5 font-medium">Dwell</th>
                <th className="text-left px-5 py-2.5 font-medium">Status</th>
                <th className="text-left px-5 py-2.5 font-medium">Referrer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {recent.map((r) => {
                const sceneLabel = r.max_scene >= 0 ? SCENE_LABELS[r.max_scene] || `Scene ${r.max_scene}` : '—';
                const refHost = r.referrer ? r.referrer.replace(/^https?:\/\//, '').split('/')[0] : '(direct)';
                return (
                  <tr key={r.session_id} className="hover:bg-slate-800/20">
                    <td className="px-5 py-2.5 text-xs text-slate-400 whitespace-nowrap">
                      {formatDate(r.opened_at, { includeTime: true })}
                    </td>
                    <td className="px-5 py-2.5 text-sm text-slate-200">
                      {sceneLabel}
                      <span className="text-[10px] text-slate-600 ml-1.5">
                        {r.max_scene >= 0 ? `(scene ${r.max_scene + 1} / ${SCENE_LABELS.length})` : ''}
                      </span>
                    </td>
                    <td className="px-5 py-2.5 text-sm text-slate-300 tabular-nums">
                      {r.dwell_seconds > 0 ? `${Math.round(r.dwell_seconds)}s` : '—'}
                    </td>
                    <td className="px-5 py-2.5">
                      {r.completed ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-emerald-900/40 text-emerald-300 border border-emerald-700/40">
                          <CheckCircle size={11} /> Completed
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-slate-800 text-slate-400 border border-slate-700">
                          <ChevRight size={11} /> Partial
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-xs text-slate-400 truncate max-w-[200px]">
                      <Globe2 size={11} className="inline mr-1.5" />
                      {refHost}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-slate-500">
        Sessions are anonymous — we generate a fresh UUID per modal open and don't
        link them to user accounts. Aggregate-only analytics, no personally
        identifiable information stored beyond what the browser sends in its
        user agent and referrer.
      </p>
    </div>
  );
}

// ─── KPI card ──────────────────────────────────────────────────────

function KpiCard({ icon: Ic, label, value, tone }) {
  const toneStyle = {
    blue:    { bg: 'bg-blue-900/30',    fg: 'text-blue-300',    border: 'border-blue-800/40' },
    emerald: { bg: 'bg-emerald-900/30', fg: 'text-emerald-300', border: 'border-emerald-800/40' },
    indigo:  { bg: 'bg-indigo-900/30',  fg: 'text-indigo-300',  border: 'border-indigo-800/40' },
    amber:   { bg: 'bg-amber-900/30',   fg: 'text-amber-300',   border: 'border-amber-800/40' },
  }[tone] || { bg: 'bg-slate-800/60', fg: 'text-slate-200', border: 'border-slate-700' };

  return (
    <div className={`rounded-2xl p-4 border ${toneStyle.border} bg-slate-900`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          {label}
        </p>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${toneStyle.bg} ${toneStyle.fg}`}>
          <Ic size={14} />
        </div>
      </div>
      <p className="text-2xl font-bold text-white tabular-nums">{value}</p>
    </div>
  );
}

// ─── Funnel chart ──────────────────────────────────────────────────

function FunnelChart({ funnel, totalOpens }) {
  // funnel is a JSONB object: { "0": 142, "1": 120, ... }. Convert to an
  // array indexed by scene number, defaulting to 0 for missing scenes.
  const counts = SCENE_LABELS.map((_, i) => Number(funnel[String(i)] || 0));

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm font-semibold text-white">Scene drop-off funnel</p>
          <p className="text-xs text-slate-500 mt-0.5">
            How many unique sessions reached each scene. The bigger the drop,
            the more visitors lost themselves at that moment.
          </p>
        </div>
        <div className="text-xs text-slate-500">
          Baseline: {totalOpens.toLocaleString()} opens
        </div>
      </div>

      <div className="space-y-2.5">
        {SCENE_LABELS.map((label, i) => {
          const c = counts[i];
          const pct = totalOpens > 0 ? Math.round((c / totalOpens) * 100) : 0;
          const prevCount = i === 0 ? totalOpens : counts[i - 1];
          // Drop relative to the previous scene — useful for spotting
          // "scene 3 is the killer".
          const dropPct = prevCount > 0 ? Math.round(((prevCount - c) / prevCount) * 100) : 0;
          return (
            <div key={label}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-slate-300 font-medium">
                  <span className="text-slate-500 mr-1.5">{i + 1}.</span>
                  {label}
                </span>
                <span className="text-slate-400 tabular-nums">
                  {c.toLocaleString()} session{c === 1 ? '' : 's'}
                  <span className="text-slate-600 ml-2">({pct}%)</span>
                  {i > 0 && dropPct > 0 && (
                    <span className="ml-2 text-red-400">−{dropPct}%</span>
                  )}
                </span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(148,163,184,0.10)' }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${pct}%`,
                    background:
                      i === SCENE_LABELS.length - 1
                        ? 'linear-gradient(90deg, #10B981 0%, #059669 100%)'
                        : `linear-gradient(90deg, rgba(249,115,22,${0.4 + i * 0.10}) 0%, rgba(234,88,12,${0.5 + i * 0.10}) 100%)`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Referrer breakdown ────────────────────────────────────────────

function ReferrerBreakdown({ byReferrer, totalOpens }) {
  const entries = Object.entries(byReferrer)
    .sort((a, b) => Number(b[1]) - Number(a[1]))
    .slice(0, 10);

  if (entries.length === 0) return null;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
      <p className="text-sm font-semibold text-white mb-1">Top referrers</p>
      <p className="text-xs text-slate-500 mb-4">
        Where the demo viewers came from. "(direct)" means no referrer was sent
        (typed URL, bookmark, or private-browsing).
      </p>
      <div className="space-y-2">
        {entries.map(([host, count]) => {
          const c = Number(count);
          const pct = totalOpens > 0 ? Math.round((c / totalOpens) * 100) : 0;
          return (
            <div key={host} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-300 truncate">
                  <Globe2 size={11} className="inline mr-1.5 text-slate-500" />
                  {host || '(direct)'}
                </p>
                <div className="h-1.5 mt-1 rounded-full overflow-hidden" style={{ background: 'rgba(148,163,184,0.10)' }}>
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${pct}%`,
                      background: 'linear-gradient(90deg, #6366F1 0%, #4F46E5 100%)',
                    }}
                  />
                </div>
              </div>
              <p className="text-xs text-slate-300 tabular-nums w-20 text-right">
                {c} <span className="text-slate-600">({pct}%)</span>
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
